import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { companyData } from '../mock';
import { CheckCircle2, Target, Users, Lightbulb } from 'lucide-react';

const About = () => {
  return (
    <div className="pt-20">
      {/* Hero Section */}
      <HeroSection />

      {/* Mission Section */}
      <MissionSection />

      {/* Advantages with Numbers */}
      <AdvantagesSection />

      {/* Commitments */}
      <CommitmentsSection />

      {/* Team Excellence */}
      <TeamSection />
    </div>
  );
};

const HeroSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-[#D4EDF7] to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <motion.h1 
            className="text-5xl md:text-7xl font-bold text-[#004C97] mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            About Randovate Labs
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed"
          >
            {companyData.description}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

const MissionSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-[#004C97] mb-6">
              Our Mission
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              {companyData.about.mission}
            </p>
            <div className="space-y-4">
              {companyData.about.expertise.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="text-[#004C97] flex-shrink-0 mt-1" size={24} />
                  <span className="text-gray-700">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="aspect-square bg-gradient-to-br from-[#004C97] to-[#003366] rounded-3xl p-12 flex items-center justify-center">
              <div className="text-center">
                <Target className="text-[#D4EDF7] mx-auto mb-6" size={80} />
                <h3 className="text-3xl font-bold text-white mb-4">Your Trusted Insurance Partner</h3>
                <p className="text-[#D4EDF7] text-lg">Since 2026</p>
              </div>
            </div>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
              className="absolute -top-4 -right-4 w-24 h-24 bg-[#D4EDF7] rounded-full opacity-50 blur-xl"
            />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 15, repeat: Infinity, ease: 'linear' }}
              className="absolute -bottom-4 -left-4 w-32 h-32 bg-[#004C97] rounded-full opacity-30 blur-xl"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const AdvantagesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-[#D4EDF7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#004C97] mb-4">
            Our Competitive Advantages
          </h2>
          <p className="text-gray-700 text-lg">What sets us apart in the insurance domain</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {companyData.advantages.map((advantage, index) => (
            <motion.div
              key={advantage.number}
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -10, boxShadow: '0 20px 40px rgba(0,76,151,0.2)' }}
              className="bg-white p-8 rounded-2xl shadow-lg relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 text-8xl font-bold text-[#D4EDF7] opacity-50 -mr-4 -mt-4 group-hover:scale-110 transition-transform">
                {advantage.number}
              </div>
              <div className="relative z-10">
                <div className="text-3xl font-bold text-[#004C97] mb-4">{advantage.number}</div>
                <h3 className="text-xl font-bold text-[#004C97] mb-3">{advantage.title}</h3>
                <p className="text-gray-600 leading-relaxed">{advantage.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const CommitmentsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-[#004C97] mb-4">
            Our Commitments
          </h2>
          <p className="text-gray-700 text-lg">Building trust through unwavering dedication</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {companyData.commitments.map((commitment, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-br from-[#004C97] to-[#003366] p-6 rounded-xl flex items-center gap-4 shadow-lg"
            >
              <CheckCircle2 className="text-[#D4EDF7] flex-shrink-0" size={28} />
              <span className="text-white font-medium">{commitment}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TeamSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-white to-[#D4EDF7]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <Users className="text-[#004C97] mx-auto mb-6" size={64} />
          <h2 className="text-4xl md:text-5xl font-bold text-[#004C97] mb-6">
            A Team Built for Excellence
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Our highly skilled professionals bring deep expertise in the insurance domain, 
            combining technical excellence with industry knowledge to deliver exceptional results for our clients.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default About;